import gsap from 'gsap';
import { BALANCE } from '@2k48/shared';

/**
 * Motion policy for the whole client: one place that decides how long things
 * take, whether they move at all, and how tweens get cleaned up.
 */

let systemReduced = false;
let preferenceReduced = false;

/**
 * `gsap.matchMedia()` rather than a bare `window.matchMedia` check, because it
 * also *reverts* the animations it created when the setting changes — a plain
 * boolean would leave whatever was already running to finish at full speed.
 */
const mm = gsap.matchMedia();
mm.add('(prefers-reduced-motion: reduce)', () => {
  systemReduced = true;
  return () => {
    systemReduced = false;
  };
});

/**
 * The in-app "Menos animación" switch.
 *
 * OR'd with the OS setting rather than replacing it: someone who asked the
 * operating system for reduced motion has already answered this question, and
 * an app default of "full motion" must not be able to override that answer.
 * The switch can therefore only ever add restraint, never remove it.
 */
export function setReducedMotionPreference(enabled: boolean): void {
  preferenceReduced = enabled;
  applyMotionTokens();
}

export function prefersReducedMotion(): boolean {
  return systemReduced || preferenceReduced;
}

/**
 * Every duration in the client goes through here.
 *
 * Under reduced motion the multiplier is ~0 rather than 0 exactly: tweens still
 * run, still fire their callbacks and still land on their final values, so the
 * code path is identical and there is no "if reduced, skip the animation and
 * also remember to apply the end state by hand" branch to get wrong.
 */
export function dur(seconds: number): number {
  return prefersReducedMotion() ? seconds * BALANCE.motion.reducedMotionScale : seconds;
}

/**
 * Push the motion values from `shared/balance.ts` into CSS custom properties,
 * so a CSS transition and a GSAP tween are tuned by the same numbers. Without
 * this, `balance.ts` would only be the single source of truth for half the app.
 */
export function applyMotionTokens(root: HTMLElement = document.documentElement): void {
  const { motion } = BALANCE;
  root.style.setProperty('--motion-move', `${dur(motion.moveSec)}s`);
  root.style.setProperty('--motion-merge', `${dur(motion.mergeSec)}s`);
  root.style.setProperty('--motion-spawn', `${dur(motion.spawnSec)}s`);
  root.style.setProperty('--motion-remove', `${dur(motion.removeSec)}s`);
  root.style.setProperty('--motion-glow', `${dur(motion.glowSec)}s`);
}

export interface MotionScope {
  /** Run a function with every tween it creates owned by this scope. */
  add(fn: () => void): void;
  /** Kill every tween created in this scope and undo inline styles it set. */
  destroy(): void;
  readonly context: gsap.Context;
}

/**
 * A `gsap.context()` per mounted view.
 *
 * This is the fix for the classic slow leak: a view is torn down, its DOM goes
 * away, but the tweens that were animating it are still on GSAP's ticker,
 * writing to detached nodes. A few navigations later the game is doing several
 * times the work it appears to be doing, and the only symptom is that it "gets
 * worse after a few minutes". `ctx.revert()` on unmount ends that.
 */
export function createMotionScope(root?: Element): MotionScope {
  const context = gsap.context(() => {}, root);

  return {
    context,
    add(fn: () => void) {
      context.add(fn);
    },
    destroy() {
      context.revert();
    },
  };
}

// ---------------------------------------------------------------------------
// Reusable directional enter/exit.
//
// Every view, overlay and toast in the app moves in one of four directions and
// fades. Before these helpers each screen would have spelled out its own
// `fromTo` with its own duration, easing and axis maths, and the app would have
// drifted into a dozen slightly different entrances. The offset-to-axis mapping
// in particular is the part that gets written backwards by hand.
// ---------------------------------------------------------------------------

export type MotionDirection = 'left' | 'right' | 'up' | 'down';

export interface DirectionalOptions {
  /** Travel in CSS px. Defaults to `BALANCE.motion.ui.offsetPx`. */
  distance?: number;
  duration?: number;
  stagger?: number;
  delay?: number;
  onComplete?: () => void;
}

export interface MotionActionHandle<T = unknown> {
  update?(value: T): void;
  destroy(): void;
}

/**
 * The direction names the side the element comes *from* on enter, and the side
 * it leaves *towards* on exit — so `enterFrom(el, 'left')` and
 * `exitTo(el, 'left')` are mirror images rather than the same tween twice.
 */
function offsetFor(direction: MotionDirection, distance: number): gsap.TweenVars {
  switch (direction) {
    case 'left':
      return { x: -distance, y: 0 };
    case 'right':
      return { x: distance, y: 0 };
    case 'up':
      return { x: 0, y: -distance };
    case 'down':
      return { x: 0, y: distance };
  }
}

type MotionTargets = gsap.TweenTarget;

/** Slide + fade in from `direction`, landing on the element's natural position. */
export function enterFrom(
  targets: MotionTargets,
  direction: MotionDirection,
  options: DirectionalOptions = {},
): gsap.core.Tween {
  const { ui } = BALANCE.motion;
  const distance = options.distance ?? ui.offsetPx;

  gsap.killTweensOf(targets);
  return gsap.fromTo(
    targets,
    { opacity: 0, ...offsetFor(direction, distance) },
    {
      opacity: 1,
      x: 0,
      y: 0,
      duration: dur(options.duration ?? ui.enterSec),
      stagger: dur(options.stagger ?? 0),
      delay: dur(options.delay ?? 0),
      ease: ui.ease.enter,
      // Handing the element back to CSS afterwards: a leftover inline
      // transform would win over any class the view applies later.
      clearProps: 'opacity,transform',
      ...(options.onComplete ? { onComplete: options.onComplete } : {}),
    },
  );
}

/**
 * Slide + fade out towards `direction`. Deliberately does not clear props: the
 * caller is unmounting or hiding the element, and clearing would flash it back
 * to full opacity for a frame before it goes.
 */
export function exitTo(
  targets: MotionTargets,
  direction: MotionDirection,
  options: DirectionalOptions = {},
): gsap.core.Tween {
  const { ui } = BALANCE.motion;
  const distance = options.distance ?? ui.offsetPx;

  gsap.killTweensOf(targets);
  return gsap.to(targets, {
    opacity: 0,
    ...offsetFor(direction, distance),
    duration: dur(options.duration ?? ui.exitSec),
    stagger: dur(options.stagger ?? 0),
    delay: dur(options.delay ?? 0),
    ease: ui.ease.exit,
    ...(options.onComplete ? { onComplete: options.onComplete } : {}),
  });
}

/**
 * Stagger for menus and short lists.
 *
 * Past `staggerMaxItems` the stagger is dropped and the whole list moves as one
 * block. A 50-row ranking staggered at 45ms would take over two seconds to
 * finish arriving, and the rows at the bottom would still be animating while
 * the user is already scrolling them.
 */
export function staggerIn(
  targets: MotionTargets,
  direction: MotionDirection = 'up',
  options: DirectionalOptions = {},
): gsap.core.Tween {
  const { ui } = BALANCE.motion;
  const count = gsap.utils.toArray(targets).length;
  const stagger = count > ui.staggerMaxItems ? 0 : (options.stagger ?? ui.staggerSec);

  return enterFrom(targets, direction, { ...options, stagger });
}

/**
 * One compositor-only entrance for every Svelte route. Views opt into no
 * per-screen ticker: this short timeline is created on mount and killed on the
 * next navigation. The CRT layers live outside the animated subtree.
 */
export function animateViewEntrance(root: HTMLElement): gsap.core.Timeline {
  const { ui } = BALANCE.motion;
  const items = [
    ...root.querySelectorAll<HTMLElement>(
      ':scope > .view__header, :scope > .view__body, :scope > .view__footer, :scope > .menu__brand, :scope > .menu__actions, :scope > .menu__footer',
    ),
  ];

  gsap.killTweensOf([root, ...items]);
  const timeline = gsap.timeline();

  if (prefersReducedMotion()) {
    return timeline.fromTo(
      root,
      { opacity: 0 },
      { opacity: 1, duration: dur(0.12), ease: 'none', clearProps: 'opacity' },
    );
  }

  timeline.fromTo(
    root,
    { opacity: 0 },
    { opacity: 1, duration: ui.viewFadeSec, ease: BALANCE.motion.ease.fade, clearProps: 'opacity' },
  );

  if (items.length > 0) {
    // Alternating sides, so the blocks of a screen read as assembling rather
    // than as one slab sliding in.
    timeline.add(
      gsap.fromTo(
        items,
        { opacity: 0, x: (index: number) => (index % 2 === 0 ? -ui.offsetPx : ui.offsetPx) },
        {
          opacity: 1,
          x: 0,
          duration: ui.enterSec,
          stagger: ui.staggerSec,
          ease: ui.ease.enter,
          clearProps: 'opacity,transform',
        },
      ),
      0,
    );
  }

  return timeline;
}

// ---------------------------------------------------------------------------
// Svelte actions for state that mounts inside an already-mounted view.
//
// Route entrance cannot see these nodes: a reconnect panel, a result screen or
// a tab page appears minutes after the view was created. Actions make the DOM
// node itself own that one-shot timeline and revert it when Svelte removes the
// state. They never delay unmounting and therefore never govern interaction or
// phase timing.
// ---------------------------------------------------------------------------

export interface OverlayMotionOptions {
  /** Stronger centre pop for short flashes such as GO. */
  emphasis?: boolean;
}

/**
 * Buttons already own a CSS `transform` transition for their pressed state.
 * Driving that same property from GSAP makes the browser interpolate every
 * animation tick a second time, which looks like a jump or flicker. They still
 * fade with the rest of the overlay; only non-controls receive travel/scale.
 */
function animateOverlayChildren(
  timeline: gsap.core.Timeline,
  children: HTMLElement[],
  options: { at: number; distance: number; emphasis?: boolean },
): void {
  const reduced = prefersReducedMotion();
  const duration = dur(reduced ? 0.1 : BALANCE.motion.ui.enterSec);
  const stagger = reduced ? 0 : dur(BALANCE.motion.ui.staggerSec);

  children.forEach((child, index) => {
    const isButton = child.matches('button, .btn');
    const from =
      reduced || isButton
        ? { opacity: 0 }
        : {
            opacity: 0,
            y: options.distance,
            scale: options.emphasis ? 0.78 : 1,
          };
    const to =
      reduced || isButton
        ? {
            opacity: 1,
            duration,
            ease: reduced ? 'none' : BALANCE.motion.ui.ease.enter,
            clearProps: 'opacity',
          }
        : {
            opacity: 1,
            y: 0,
            scale: 1,
            duration,
            ease: options.emphasis ? BALANCE.motion.ease.pop : BALANCE.motion.ui.ease.enter,
            clearProps: 'opacity,transform',
          };

    timeline.fromTo(child, from, to, options.at + index * stagger);
  });
}

export function animateOverlay(
  node: HTMLElement,
  options: OverlayMotionOptions = {},
): MotionActionHandle<OverlayMotionOptions> {
  const scope = createMotionScope(node);

  const run = (current: OverlayMotionOptions): void => {
    const children = [...node.children] as HTMLElement[];
    scope.add(() => {
      gsap.killTweensOf([node, ...children]);
      const timeline = gsap.timeline();

      timeline.fromTo(
        node,
        { opacity: 0 },
        {
          opacity: 1,
          duration: dur(prefersReducedMotion() ? 0.1 : BALANCE.motion.ui.viewFadeSec),
          ease: prefersReducedMotion() ? 'none' : BALANCE.motion.ease.fade,
          clearProps: 'opacity',
        },
      );

      if (children.length === 0) return;
      animateOverlayChildren(timeline, children, {
        at: prefersReducedMotion() ? 0 : 0.02,
        distance: BALANCE.motion.ui.offsetPx * 0.55,
        emphasis: current.emphasis,
      });
    });
  };

  run(options);
  return {
    update(next) {
      options = next;
    },
    destroy: () => scope.destroy(),
  };
}

/** Result panels get a slightly longer stagger, but are interactive at frame 1. */
export function animateResult(node: HTMLElement): MotionActionHandle {
  const scope = createMotionScope(node);
  const children = [...node.children] as HTMLElement[];

  scope.add(() => {
    gsap.killTweensOf([node, ...children]);
    const timeline = gsap.timeline();
    timeline.fromTo(
      node,
      { opacity: 0 },
      {
        opacity: 1,
        duration: dur(prefersReducedMotion() ? 0.1 : BALANCE.motion.ui.viewFadeSec),
        ease: prefersReducedMotion() ? 'none' : BALANCE.motion.ease.fade,
        clearProps: 'opacity',
      },
    );
    animateOverlayChildren(timeline, children, {
      at: prefersReducedMotion() ? 0 : 0.03,
      distance: BALANCE.motion.ui.offsetPx,
    });
  });

  return { destroy: () => scope.destroy() };
}

export interface TabMotionState {
  value: string;
}

/** Animate the selected tab, not its colour/background, so switching is composite-only. */
export function animateTabSelection(
  node: HTMLElement,
  state: TabMotionState,
): MotionActionHandle<TabMotionState> {
  const scope = createMotionScope(node);
  let currentValue = state.value;

  const tabs = (): HTMLElement[] => [
    ...node.querySelectorAll<HTMLElement>('[role="tab"]:not(:disabled)'),
  ];

  scope.add(() => staggerIn(tabs(), 'up', { distance: BALANCE.motion.ui.offsetPx * 0.4 }));

  const select = (value: string): void => {
    const active = node.querySelector<HTMLElement>(`[data-tab-id="${CSS.escape(value)}"]`);
    if (!active) return;
    scope.add(() => {
      gsap.killTweensOf(active);
      if (prefersReducedMotion()) {
        gsap.fromTo(active, { opacity: 0.72 }, { opacity: 1, duration: dur(0.1) });
        return;
      }
      gsap.fromTo(
        active,
        { scale: 0.93, opacity: 0.74 },
        {
          scale: 1,
          opacity: 1,
          duration: dur(0.18),
          ease: BALANCE.motion.ease.pop,
          clearProps: 'opacity,transform',
        },
      );
    });
  };

  return {
    update(next) {
      if (next.value === currentValue) return;
      currentValue = next.value;
      select(next.value);
    },
    destroy: () => scope.destroy(),
  };
}

/** One block transition for the content associated with a selected tab. */
export function animateTabPanel(node: HTMLElement): MotionActionHandle {
  const scope = createMotionScope(node);
  scope.add(() => enterFrom(node, 'right', { distance: BALANCE.motion.ui.offsetPx * 0.65 }));
  return { destroy: () => scope.destroy() };
}

export { gsap };
